import { motion } from 'framer-motion';
import { Check, Phone, Mail, Calendar, MapPin, Users, Plane, Train, Bus, Hotel, ArrowLeft, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';

export default function BookingConfirmation({ 
  bookingType = 'package', // 'package', 'flight', 'train', 'bus', 'hotel', 'trip'
  bookingDetails = {},
  onClose 
}) {
  const getIcon = () => {
    switch(bookingType) {
      case 'flight': return Plane;
      case 'train': return Train;
      case 'bus': return Bus;
      case 'hotel': return Hotel;
      default: return MapPin;
    }
  };

  const getTitle = () => {
    switch(bookingType) {
      case 'flight': return 'Flight Booking Confirmed!';
      case 'train': return 'Train Booking Confirmed!';
      case 'bus': return 'Bus Booking Confirmed!';
      case 'hotel': return 'Hotel Booking Confirmed!';
      case 'trip': return 'Trip Request Submitted!';
      default: return 'Booking Confirmed!';
    }
  };

  const Icon = getIcon();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="booking-confirmation-overlay"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: 'spring', duration: 0.5 }}
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="booking-confirmation-modal"
      >
        {/* Success Header */}
        <div className="bg-gradient-to-br from-teal-500 to-teal-600 p-8 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.4, type: 'spring' }}
              className="w-16 h-16 rounded-full bg-white flex items-center justify-center"
            >
              <Check className="w-8 h-8 text-teal-600" strokeWidth={3} />
            </motion.div>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-2xl md:text-3xl font-semibold text-white mb-2"
          >
            Congratulations! 🎉
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-teal-100 text-lg"
          >
            {getTitle()}
          </motion.p>
        </div>

        {/* Booking Details */}
        <div className="p-6 md:p-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-slate-50 rounded-xl p-4 mb-6"
          >
            {bookingDetails.bookingId && (
              <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-200">
                <span className="text-sm text-slate-500">Booking Reference</span>
                <span className="font-mono font-bold text-teal-700">{bookingDetails.bookingId}</span>
              </div>
            )}
            
            {bookingDetails.name && (
              <div className="flex items-center gap-3 py-2">
                <Icon className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="font-medium text-slate-900">{bookingDetails.name}</p>
                  {bookingDetails.details && (
                    <p className="text-sm text-slate-500">{bookingDetails.details}</p>
                  )}
                </div>
              </div>
            )}

            {bookingDetails.date && (
              <div className="flex items-center gap-3 py-2">
                <Calendar className="w-5 h-5 text-slate-400" />
                <span className="text-slate-700">{bookingDetails.date}</span>
              </div>
            )}

            {bookingDetails.travelers && (
              <div className="flex items-center gap-3 py-2">
                <Users className="w-5 h-5 text-slate-400" />
                <span className="text-slate-700">{bookingDetails.travelers} Traveler(s)</span>
              </div>
            )}

            {bookingDetails.price && (
              <div className="flex items-center justify-between pt-3 mt-3 border-t border-slate-200">
                <span className="text-slate-500">Total Amount</span>
                <span className="text-xl font-bold text-teal-700">₹{bookingDetails.price.toLocaleString()}</span>
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-center mb-6"
          >
            <p className="text-slate-600 mb-4">
              Our travel expert will contact you within <span className="font-semibold text-teal-700">24 hours</span> to confirm your booking and assist with any requirements.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
              <a href="tel:+919281410305" className="flex items-center gap-2 text-slate-600 hover:text-teal-600 transition-colors">
                <Phone className="w-4 h-4" />
                +91 92814 10305
              </a>
              <a href="mailto:info@id4utravels.com" className="flex items-center gap-2 text-slate-600 hover:text-teal-600 transition-colors">
                <Mail className="w-4 h-4" />
                info@id4utravels.com
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="flex flex-col sm:flex-row gap-3"
          >
            <Link to="/" className="flex-1">
              <Button 
                className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl py-3"
                data-testid="go-home-btn"
              >
                <Home className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
            <Button 
              variant="outline"
              onClick={onClose}
              className="flex-1 rounded-xl py-3"
              data-testid="continue-browsing-btn"
            >
              Continue Browsing
            </Button>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}
